import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import privaxiaLogo from '@/assets/privaxia-logo.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('As passwords não coincidem');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('A password deve ter pelo menos 8 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name
      });
      
      toast.success('Conta criada com sucesso! Faça login.');
      navigate('/login-local');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1765366417030-16d9765d920a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBkYXRhJTIwc2VjdXJpdHl8ZW58MHx8fHwxNzY4OTk3NjY2fDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/90 to-[#0F172A]/70" />
        <div className="relative z-10 flex flex-col justify-center items-start p-12 text-white">
          <img src={privaxiaLogo} alt="Privaxia Logo" className="w-20 h-20 object-contain mb-6" />
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Criar Conta
          </h1>
          <p className="text-xl text-slate-300 max-w-md">
            Registe-se na plataforma de gestão de consentimentos RGPD.
          </p>
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <img src={privaxiaLogo} alt="Privaxia Logo" className="w-16 h-16 object-contain" />
            </div>
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Criar Nova Conta
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Preencha os dados para criar a sua conta
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 space-y-6">
            <div>
              <Label htmlFor="name">Nome Completo</Label>
              <Input
                id="name"
                data-testid="register-name-input"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="mt-2"
                placeholder="João Silva"
              />
            </div>
            
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="register-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="mt-2"
                placeholder="joao@exemplo.com"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="register-password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                className="mt-2"
                placeholder="Mínimo 8 caracteres"
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirmar Password</Label>
              <Input
                id="confirmPassword"
                data-testid="register-confirm-password-input"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                required
                className="mt-2"
                placeholder="Repita a password"
              />
            </div>

            <Button
              data-testid="register-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#10B981] hover:bg-[#10B981]/90 text-white font-medium"
            >
              {loading ? 'A criar conta...' : 'Criar Conta'}
            </Button>
            
            <div className="text-center text-sm">
              <span className="text-slate-600">Já tem conta? </span>
              <button
                type="button"
                onClick={() => navigate('/login-local')}
                className="text-[#10B981] hover:underline font-medium"
              >
                Faça login
              </button>
            </div>
          </form>

          <div className="text-center text-xs text-slate-500">
            © 2026 Privaxia. Todos os direitos reservados.
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;